import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import maplibregl, {
  type GeoJSONSource,
  type ImageSource,
  type Map as MapLibreMap,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import { createObscurationOverlay, type ObscurationOverlay } from '../features/obscuration/overlay';
import { eclipseMapData } from '../lib/eclipse';
import type { WeatherLayer } from '../lib/weather';
import type { Coordinate, GeoJsonLineString } from '../types';

interface MapLayers {
  clouds: boolean;
  obscuration: boolean;
  traffic: boolean;
  terrain: boolean;
  totality: boolean;
}

interface MapViewProps {
  layers: MapLayers;
  selected: Coordinate | null;
  origin: Coordinate | null;
  obscurationTime: Date;
  route: GeoJsonLineString | null;
  weather: WeatherLayer | null;
  pickingOrigin: boolean;
  onSelect: (coordinate: Coordinate) => void;
}

const emptyCollection = { type: 'FeatureCollection', features: [] } as const;
const weatherCoverage: [[number, number], [number, number], [number, number], [number, number]] = [
  [-11, 46.8],
  [7, 46.8],
  [7, 36],
  [-11, 36],
];

interface CloudSurface {
  url: string;
  coordinates: [[number, number], [number, number], [number, number], [number, number]];
}

function emptyCloudSurface(): CloudSurface {
  return { url: '/weather/transparent.png', coordinates: weatherCoverage };
}

function cloudSurface(layer: WeatherLayer | null): CloudSurface {
  if (!layer) return emptyCloudSurface();
  const { west, south, east, north } = layer.bounds;
  return {
    url: layer.frame.url,
    coordinates: [
      [west, north],
      [east, north],
      [east, south],
      [west, south],
    ],
  };
}

function setLayerVisibility(map: MapLibreMap, layerId: string, visible: boolean) {
  if (map.getLayer(layerId)) {
    map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
  }
}

function coordinateFeature(coordinate: Coordinate | null) {
  if (!coordinate) return emptyCollection;
  return {
    type: 'FeatureCollection' as const,
    features: [
      {
        type: 'Feature' as const,
        properties: {},
        geometry: {
          type: 'Point' as const,
          coordinates: [coordinate.lon, coordinate.lat],
        },
      },
    ],
  };
}

function createTrafficIncidentIcon(): ImageData {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas is unavailable');

  context.beginPath();
  context.moveTo(size / 2, 3);
  context.lineTo(size - 3, size / 2);
  context.lineTo(size / 2, size - 3);
  context.lineTo(3, size / 2);
  context.closePath();
  context.fillStyle = '#c9304d';
  context.fill();
  context.lineWidth = 5;
  context.strokeStyle = '#fff4d6';
  context.stroke();

  context.fillStyle = '#fff4d6';
  context.font = '900 38px Arial, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText('!', size / 2, size / 2 + 2);

  return context.getImageData(0, 0, size, size);
}

export function MapView({
  layers,
  selected,
  origin,
  obscurationTime,
  route,
  weather,
  pickingOrigin,
  onSelect,
}: MapViewProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const obscurationRef = useRef<ObscurationOverlay | null>(null);
  const selectRef = useRef(onSelect);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const mapLabel = t('map.label');

  selectRef.current = onSelect;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: 'https://tiles.openfreemap.org/styles/liberty',
      center: [-2.4, 41.7],
      zoom: 5.3,
      minZoom: 4,
      maxZoom: 16,
      attributionControl: false,
      cooperativeGestures: true,
    });
    mapRef.current = map;
    const loadTimeout = window.setTimeout(() => setLoadError(true), 15_000);
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'bottom-right');
    map.addControl(
      new maplibregl.AttributionControl({
        compact: true,
        customAttribution: 'Weather: DWD · TomTom',
      }),
      'bottom-right',
    );

    map.on('load', () => {
      window.clearTimeout(loadTimeout);
      try {
        map.addImage('traffic-incident-warning', createTrafficIncidentIcon(), { pixelRatio: 2 });
        map.addSource('eclipse', { type: 'geojson', data: eclipseMapData });
        map.addLayer({
          id: 'totality-fill',
          type: 'fill',
          source: 'eclipse',
          filter: ['==', ['get', 'kind'], 'totality'],
          paint: { 'fill-color': '#ff5b35', 'fill-opacity': 0.18 },
        });
        map.addLayer({
          id: 'totality-border',
          type: 'line',
          source: 'eclipse',
          filter: ['==', ['get', 'kind'], 'totality'],
          paint: { 'line-color': '#ff5b35', 'line-width': 2.5, 'line-opacity': 0.96 },
        });
        map.addLayer({
          id: 'centerline',
          type: 'line',
          source: 'eclipse',
          filter: ['==', ['get', 'kind'], 'centerline'],
          paint: { 'line-color': '#fff4d6', 'line-width': 1.4, 'line-dasharray': [2, 2] },
        });

        const initialCloudSurface = emptyCloudSurface();
        map.addSource('weather', {
          type: 'image',
          url: initialCloudSurface.url,
          coordinates: initialCloudSurface.coordinates,
        });
        map.addLayer({
          id: 'cloud-surface',
          type: 'raster',
          source: 'weather',
          paint: {
            'raster-opacity': 1,
            'raster-contrast': 0.16,
            'raster-saturation': 0.08,
            'raster-fade-duration': 500,
            'raster-resampling': 'linear',
          },
        });

        map.addSource('terrain-dem', {
          type: 'raster-dem',
          tiles: ['https://elevation-tiles-prod.s3.amazonaws.com/terrarium/{z}/{x}/{y}.png'],
          tileSize: 256,
          maxzoom: 15,
          encoding: 'terrarium',
          attribution: 'Elevation © Mapzen terrain sources',
        });
        map.addLayer({
          id: 'terrain-hillshade',
          type: 'hillshade',
          source: 'terrain-dem',
          layout: { visibility: 'none' },
          paint: {
            'hillshade-shadow-color': '#11100d',
            'hillshade-highlight-color': '#ff5b35',
            'hillshade-exaggeration': 0.45,
          },
        });
        // Terrain remains legible beneath clouds; eclipse geometry stays crisp above both.
        map.moveLayer('terrain-hillshade', 'totality-fill');
        map.moveLayer('cloud-surface', 'totality-fill');
        obscurationRef.current = createObscurationOverlay(map);

        map.addSource('traffic-flow', {
          type: 'vector',
          tiles: [`${window.location.origin}/api/traffic/flow/{z}/{x}/{y}.pbf`],
          minzoom: 7,
          maxzoom: 20,
        });
        map.addLayer({
          id: 'traffic-flow-lines',
          type: 'line',
          source: 'traffic-flow',
          'source-layer': 'Traffic flow',
          minzoom: 7,
          layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': [
              'step',
              ['coalesce', ['get', 'relative_speed'], 1],
              '#d83b55',
              0.25,
              '#d83b55',
              0.5,
              '#f1973c',
              0.75,
              '#f0d45a',
              0.9,
              '#49b675',
            ],
            'line-width': ['interpolate', ['linear'], ['zoom'], 7, 1, 14, 5],
            'line-opacity': 0.9,
          },
        });
        map.addSource('traffic-incidents', {
          type: 'vector',
          tiles: [`${window.location.origin}/api/traffic/incidents/{z}/{x}/{y}.pbf`],
          minzoom: 7,
          maxzoom: 20,
        });
        map.addLayer({
          id: 'traffic-incident-lines',
          type: 'line',
          source: 'traffic-incidents',
          'source-layer': 'Traffic incident flow',
          minzoom: 7,
          layout: { visibility: 'none' },
          paint: { 'line-color': '#cc3150', 'line-width': 5, 'line-opacity': 0.75 },
        });
        map.addLayer({
          id: 'traffic-incident-points',
          type: 'symbol',
          source: 'traffic-incidents',
          'source-layer': 'Traffic incident points',
          minzoom: 7,
          layout: {
            visibility: 'none',
            'icon-image': 'traffic-incident-warning',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 7, 0.7, 13, 1],
            'icon-allow-overlap': false,
            'icon-padding': 3,
          },
        });

        for (const source of ['selected-point', 'origin-point', 'route']) {
          map.addSource(source, { type: 'geojson', data: emptyCollection });
        }
        map.addLayer({
          id: 'route-line-outline',
          type: 'line',
          source: 'route',
          paint: { 'line-color': '#11100d', 'line-width': 8, 'line-opacity': 0.7 },
        });
        map.addLayer({
          id: 'route-line',
          type: 'line',
          source: 'route',
          paint: { 'line-color': '#62d7ed', 'line-width': 5 },
        });
        map.addLayer({
          id: 'origin-marker',
          type: 'circle',
          source: 'origin-point',
          paint: {
            'circle-radius': 8,
            'circle-color': '#62d7ed',
            'circle-stroke-color': '#fff4d6',
            'circle-stroke-width': 3,
          },
        });
        map.addLayer({
          id: 'selected-marker',
          type: 'circle',
          source: 'selected-point',
          paint: {
            'circle-radius': 9,
            'circle-color': '#ff5b35',
            'circle-stroke-color': '#11100d',
            'circle-stroke-width': 3,
          },
        });
        setReady(true);
      } catch {
        setLoadError(true);
      }
    });
    map.on('click', (event) => {
      selectRef.current({ lat: event.lngLat.lat, lon: event.lngLat.lng });
    });
    const canvas = map.getCanvas();
    const selectMapCenter = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      const center = map.getCenter();
      selectRef.current({ lat: center.lat, lon: center.lng });
    };
    canvas.addEventListener('keydown', selectMapCenter);

    return () => {
      window.clearTimeout(loadTimeout);
      canvas.removeEventListener('keydown', selectMapCenter);
      obscurationRef.current?.destroy();
      obscurationRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    mapRef.current?.getCanvas().setAttribute('aria-label', mapLabel);
  }, [mapLabel, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    setLayerVisibility(map, 'totality-fill', layers.totality);
    setLayerVisibility(map, 'totality-border', layers.totality);
    setLayerVisibility(map, 'centerline', layers.totality);
    obscurationRef.current?.setVisible(layers.obscuration);
    setLayerVisibility(map, 'cloud-surface', layers.clouds);
    setLayerVisibility(map, 'traffic-flow-lines', layers.traffic);
    setLayerVisibility(map, 'traffic-incident-lines', layers.traffic);
    setLayerVisibility(map, 'traffic-incident-points', layers.traffic);
  }, [layers.clouds, layers.obscuration, layers.totality, layers.traffic, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    setLayerVisibility(map, 'terrain-hillshade', layers.terrain);
    map.setTerrain(layers.terrain ? { source: 'terrain-dem', exaggeration: 1.25 } : null);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    map.easeTo({ pitch: layers.terrain ? 42 : 0, duration: reduceMotion ? 0 : 500 });
  }, [layers.terrain, ready]);

  useEffect(() => {
    if (!ready) return;
    obscurationRef.current?.setTime(obscurationTime);
  }, [obscurationTime, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    (map.getSource('weather') as ImageSource).updateImage(cloudSurface(weather));
  }, [ready, weather]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    (map.getSource('selected-point') as GeoJSONSource).setData(coordinateFeature(selected));
    (map.getSource('origin-point') as GeoJSONSource).setData(coordinateFeature(origin));
    const routeData = route
      ? { type: 'Feature', properties: {}, geometry: route }
      : emptyCollection;
    (map.getSource('route') as GeoJSONSource).setData(routeData);
  }, [origin, ready, route, selected]);

  return (
    <div className={`map-shell ${pickingOrigin ? 'is-picking-origin' : ''}`}>
      <div ref={containerRef} className="map-canvas" aria-label={mapLabel} />
      {!ready && !loadError && (
        <div className="map-loading" role="status">
          <span className="visually-hidden">{t('map.loading')}</span>
        </div>
      )}
      {!ready && loadError && (
        <div className="map-error" role="alert">
          <span aria-hidden="true">!</span>
          <p>{t('map.error')}</p>
        </div>
      )}
    </div>
  );
}
